import { useEffect, useState } from "react";
import { X, Loader2 } from "lucide-react";
import {
  createProject,
  PROJECT_COLORS,
  PROJECT_ICONS,
  Project,
} from "@/lib/api/projects";
import { cn } from "@/lib/utils";

interface Props {
  onClose: () => void;
  onCreated: (project: Project) => void;
}

/**
 * Create Project dialog — real POST /api/projects (Phase 2 backend).
 * Closes ONLY after the backend confirms the create (201). No local
 * project is fabricated before that.
 */
export function ProjectCreateDialog({ onClose, onCreated }: Props) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [icon, setIcon] = useState(PROJECT_ICONS[0]);
  const [color, setColor] = useState(PROJECT_COLORS[0].id);
  const [instructions, setInstructions] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [busy, onClose]);

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    setError(null);
    try {
      const project = await createProject({
        name: trimmed,
        description: description.trim() || null,
        icon,
        color,
        instructions: instructions.trim() || null,
      });
      onCreated(project);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Не удалось создать проект");
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/60"
        onClick={() => !busy && onClose()}
        aria-hidden
      />
      <div className="relative w-full max-w-md rounded-xl border border-strongline bg-elevated p-5 shadow-2xl">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-medium text-primary">Новый проект</h3>
          <button
            onClick={onClose}
            disabled={busy}
            className="p-1.5 rounded-lg text-tertiary hover:text-primary hover:bg-hoverbg transition-colors"
            aria-label="Закрыть"
          >
            <X size={16} />
          </button>
        </div>

        <div className="mt-4 space-y-4">
          <div>
            <label className="block text-xs text-secondary mb-1.5" htmlFor="pj-name">
              Название
            </label>
            <input
              id="pj-name"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && void submit()}
              placeholder="Например: RealityAI"
              className="chat-search-input w-full h-9 px-3 text-sm"
            />
          </div>

          <div>
            <label className="block text-xs text-secondary mb-1.5" htmlFor="pj-desc">
              Описание
            </label>
            <input
              id="pj-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Коротко, что это за проект"
              className="chat-search-input w-full h-9 px-3 text-sm"
            />
          </div>

          <div>
            <span className="block text-xs text-secondary mb-1.5">Иконка</span>
            <div className="flex flex-wrap gap-1.5">
              {PROJECT_ICONS.map((g) => (
                <button
                  key={g}
                  onClick={() => setIcon(g)}
                  aria-label={`Иконка ${g}`}
                  aria-pressed={icon === g}
                  className={cn(
                    "w-8 h-8 rounded-lg text-base flex items-center justify-center transition-colors border",
                    icon === g
                      ? "border-accent text-accent bg-hoverbg2"
                      : "border-hairline text-secondary hover:bg-hoverbg",
                  )}
                >
                  {g}
                </button>
              ))}
            </div>
          </div>

          <div>
            <span className="block text-xs text-secondary mb-1.5">Цвет</span>
            <div className="flex flex-wrap gap-2">
              {PROJECT_COLORS.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setColor(c.id)}
                  aria-label={`Цвет ${c.id}`}
                  aria-pressed={color === c.id}
                  className={cn(
                    "w-6 h-6 rounded-full transition-transform border-2",
                    color === c.id
                      ? "border-primary scale-110"
                      : "border-transparent hover:scale-105",
                  )}
                  style={{ backgroundColor: c.hex }}
                />
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs text-secondary mb-1.5" htmlFor="pj-inst">
              Инструкции (необязательно)
            </label>
            <textarea
              id="pj-inst"
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              rows={3}
              placeholder="Контекст, который AI будет учитывать в чатах этого проекта"
              className="chat-search-input w-full px-3 py-2 text-sm resize-none"
            />
          </div>

          {error && (
            <div className="text-sm text-red-400" role="alert">
              {error}
            </div>
          )}
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button
            onClick={onClose}
            disabled={busy}
            className="h-9 px-4 rounded-lg text-sm text-secondary hover:bg-hoverbg2 hover:text-primary transition-colors"
          >
            Отмена
          </button>
          <button
            onClick={() => void submit()}
            disabled={busy || !name.trim()}
            className={cn(
              "h-9 px-4 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5",
              busy || !name.trim()
                ? "bg-hoverbg text-disabled cursor-not-allowed"
                : "bg-accent text-accent-contrast hover:opacity-90",
            )}
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            Создать
          </button>
        </div>
      </div>
    </div>
  );
}
